// ─── Canvas 공통 유틸리티 ────────────────────────────────────────────────────
// OrderEntryBox, TPAndSLBox, TPSLRatioBox, TPAndSLDragHandle에서 공통으로 사용하는
// canvas 드로잉 헬퍼 함수 모음.

// ─── 공통 레이아웃 상수 ───────────────────────────────────────────────────────
/** order line UI 박스 공통 높이 (entry, TP/SL, ratio 박스 모두 동일) */
export const BOX_HEIGHT = 24;
/** 박스 모서리 반지름 */
export const BOX_RADIUS = 4;

// ─── 공통 폰트 상수 ───────────────────────────────────────────────────────────
// 모듈 레벨 상수로 정의해 매 렌더마다 문자열 재생성을 방지한다.
export const FONT_NORMAL = 'normal normal 12px -apple-system, system-ui, sans-serif';
export const FONT_BOLD = 'normal 600 12px -apple-system, system-ui, sans-serif';

// ─── 둥근 사각형 ──────────────────────────────────────────────────────────────
/**
 * corners: 'all'(기본) | 'left'(좌측만 둥글게) | 'right'(우측만 둥글게)
 * TP/SL 박스처럼 인접 박스와 붙어 있을 때 한쪽만 둥글게 그릴 때 사용.
 */
export function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  corners: 'all' | 'left' | 'right' = 'all',
) {
  const tl = corners === 'right' ? 0 : r;
  const tr = corners === 'left' ? 0 : r;
  const br = corners === 'left' ? 0 : r;
  const bl = corners === 'right' ? 0 : r;

  ctx.beginPath();
  ctx.moveTo(x + tl, y);
  ctx.lineTo(x + w - tr, y);
  ctx.arcTo(x + w, y, x + w, y + tr, tr);
  ctx.lineTo(x + w, y + h - br);
  ctx.arcTo(x + w, y + h, x + w - br, y + h, br);
  ctx.lineTo(x + bl, y + h);
  ctx.arcTo(x, y + h, x, y + h - bl, bl);
  ctx.lineTo(x, y + tl);
  ctx.arcTo(x, y, x + tl, y, tl);
  ctx.closePath();
}

// ─── X (취소) 아이콘 ──────────────────────────────────────────────────────────
const CANCEL_ICON_R = 5.5; // 원 반지름
const CANCEL_ICON_X = 2; // X 선 반폭

export function drawCancelIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number, color: string) {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';

  ctx.beginPath();
  ctx.arc(cx, cy, CANCEL_ICON_R, 0, Math.PI * 2);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(cx - CANCEL_ICON_X, cy - CANCEL_ICON_X);
  ctx.lineTo(cx + CANCEL_ICON_X, cy + CANCEL_ICON_X);
  ctx.moveTo(cx + CANCEL_ICON_X, cy - CANCEL_ICON_X);
  ctx.lineTo(cx - CANCEL_ICON_X, cy + CANCEL_ICON_X);
  ctx.stroke();

  ctx.restore();
}

// ─── 텍스트 너비 측정 ─────────────────────────────────────────────────────────
// 오프스크린 canvas를 모듈 레벨에서 한 번만 생성해 재사용한다.
// document.createElement('canvas')는 DOM 생성 비용이 있으므로 호출마다 만들면 낭비다.
// 측정 전용이라 화면에 붙지 않으므로 전역 공유해도 부작용이 없다.
let _measureCtx: CanvasRenderingContext2D | null = null;

export function measureTextWidth(text: string, font: string): number {
  if (!_measureCtx) _measureCtx = document.createElement('canvas').getContext('2d');
  if (!_measureCtx) return text.length * 7; // SSR fallback
  _measureCtx.font = font;
  return _measureCtx.measureText(text).width;
}
